import React from 'react'
import styles from './giftCardFilter.module.scss'
import { Text } from '@sendoutcards/quantum-design-ui'
import SimpleMagnifierIcon from 'src/act/components/ContactSearch/SimpleMagnifierIcon'
import { SearchData } from './GiftCardWallet'
import GiftCardFilterField from './GiftCardFilterField'
import { useState } from 'src/hooks'
import GiftCardFilterDateField from './GiftCardFilterDateField'

type GiftCardFilterProps = {
  onSearch: (searchData: SearchData) => void
}

const GiftCardFilter: React.FC<GiftCardFilterProps> = props => {
  const { onSearch } = props

  const [isSelectingFilter, setIsSelectingFilter] = useState(false)
  const [searchData, setSearchData] = useState<SearchData>({})

  const handleSearch = <K extends keyof SearchData>(
    key: K,
    value: SearchData[K],
  ) => {
    const newSearchData = { ...searchData, [key]: value }
    setSearchData(newSearchData)
    onSearch(newSearchData)
  }

  const handleRemove = <K extends keyof SearchData>(key: K) => {
    const { [key]: removed, ...updatedSearchData } = searchData
    setSearchData(updatedSearchData)
    onSearch(updatedSearchData)
  }

  const handleSelectFilter = () => {
    if (
      searchData.paidDate !== undefined &&
      searchData.senderName !== undefined &&
      searchData.giftCardCode !== undefined &&
      searchData.giftCardAmount !== undefined
    ) {
      setIsSelectingFilter(false)
    } else {
      setIsSelectingFilter(!isSelectingFilter)
    }
  }

  return (
    <div className={styles.wrapper} onClick={handleSelectFilter}>
      <div className={styles.filterMainLabel}>
        <SimpleMagnifierIcon />
        {(searchData.paidDate === undefined ||
          searchData.senderName === undefined ||
          searchData.giftCardAmount === undefined ||
          searchData.giftCardCode === undefined) && (
          <Text type="caption" content="Search..." />
        )}
      </div>
      {searchData.senderName !== undefined && (
        <GiftCardFilterField
          label="Sender Name"
          value={searchData.senderName}
          onChange={value => {
            handleSearch('senderName', value)
          }}
          onRemove={() => handleRemove('senderName')}
        />
      )}
      {searchData.giftCardCode !== undefined && (
        <GiftCardFilterField
          label="Brand Code"
          value={searchData.giftCardCode}
          onChange={value => {
            handleSearch('giftCardCode', value)
          }}
          onRemove={() => handleRemove('giftCardCode')}
          inputStyles={{ width: '100px' }}
        />
      )}
      {searchData.giftCardAmount !== undefined && (
        <GiftCardFilterField
          label="Amount"
          value={searchData.giftCardAmount.gte ?? ''}
          onChange={value => {
            const amountRange = { gte: value, lte: value }
            handleSearch('giftCardAmount', amountRange)
          }}
          onRemove={() => handleRemove('giftCardAmount')}
          inputStyles={{ width: '70px' }}
        />
      )}
      {searchData.paidDate !== undefined && (
        <GiftCardFilterDateField
          label="Date"
          value={searchData.paidDate}
          onChange={value => {
            handleSearch('paidDate', value)
          }}
          onRemove={() => handleRemove('paidDate')}
        />
      )}
      {isSelectingFilter && (
        <div className={styles.filterTypeSelection}>
          {searchData.senderName === undefined && (
            <div
              onClick={() => {
                setSearchData({ ...searchData, senderName: '' })
              }}
            >
              <Text type="caption" content="Sender Name" />
            </div>
          )}
          {searchData.paidDate === undefined && (
            <div
              onClick={() => {
                setSearchData({ ...searchData, paidDate: {} })
              }}
            >
              <Text type="caption" content="Date" />
            </div>
          )}
          {searchData.giftCardAmount === undefined && (
            <div
              onClick={() => {
                setSearchData({ ...searchData, giftCardAmount: {} })
              }}
            >
              <Text type="caption" content="Amount" />
            </div>
          )}
          {searchData.giftCardCode === undefined && (
            <div
              onClick={() => {
                setSearchData({ ...searchData, giftCardCode: '' })
              }}
            >
              <Text type="caption" content="Brand Code" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default GiftCardFilter
